<template>
  <v-container>
    <v-window v-model="step">
      <v-window-item>
        <div class="text-h4">UNIFORM SALES & USE TAX CERTIFICATE — MULTIJURISDICTION</div>
        <p>Issued to Seller: 123.Net, Inc.</p>
        <p>Address:24700 Northwestern Hwy. Fl. 7 Southfield, MI 48075</p>
        <p>I certify that:</p>
        <v-text-field
          v-model="general.statetax_name_of_firm"
          label="Name of Firm (Buyer)"
        ></v-text-field>
        <AddressAutocomplete
          :value="statetaxAddress"
          @registerAddressChange="setAddress"
        />
      </v-window-item>
      <v-window-item>
        <div class="text-h4">Nature of Business</div>
        <p>Your business is engaged as a registered:</p>
        <v-select
          :items="companyTypes"
          v-model="general.statetax_company_type"
          label="Company Type"
        ></v-select>
        <v-text-field
          v-if="general.statetax_company_type === 'other'"
          v-model="general.statetax_other_type"
          label="Specify type of company"
        ></v-text-field>
        <p>
          and is registered with the below listed states and cities within which your firm would
          deliver purchases to us and that any such purchases are for wholesale, resale,
          ingredients or components of a new product or service to be resold, leased, or rented in
          the normal course of business. We are in the business of wholesaling, retailing,
          manufacturing, leasing (renting) the following
        </p>
        <v-text-field
          label="Description of Business"
          v-model="general.statetax_desc_of_business"
        ></v-text-field>
        <v-text-field
          :label="descriptionOfTaxableItems"
          v-model="general.statetax_purchased_desc"
        ></v-text-field>
      </v-window-item>
      <v-window-item>
        <div class="text-h4">State exemption IDs</div>
        <p>
          The below-listed states have indicated that this form of certificate is acceptable,
          subject to the notes on pages 2-4. The issuer and the recipient have the responsibility
          of determining the proper use of this certificate under applicable laws in each state,
          as these may change from time to time.
        </p>
        <v-container>
          <v-row
            v-for="(state, i) in addedStates"
            :key="state"
          >
            <v-col>
              <v-autocomplete
                v-model="addedStates[i]"
                label="State"
                :items="[state, ...filteredStates]"
              >
                <template v-slot:selection="data">
                  {{ data.item }}
                  <sup v-if="superscripts[data.item]">{{ superscripts[data.item] }}</sup>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col>
              <v-text-field
                v-if="addedStates[i] != null"
                v-model="general[`statetax_${state.toLowerCase()}_id`]"
                label="State Registration, Seller's Permit, or ID Number of Purchaser"
              ></v-text-field>
            </v-col>
            <v-col cols="auto">
              <v-btn
                color="error"
                text
                @click="deleteState(i)"
              >Delete</v-btn>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-btn
                color="third"
                class="white--text"
                @click="addState"
              >
                Add State ID
              </v-btn>
            </v-col>
          </v-row>
          <v-row>
            <p>
              I further certify that if any property or service so purchased tax free is used
              or consumed by the firm as to make it subject to a Sales or use Tax we will pay the
              tax due directly to the proper taxing authority when state law so provides or inform
              the seller for added tax billing. This certificate shall be a part of each order
              which we may hereafter give to you, unless otherwise specified, and shall be valid
              until canceled by us in writing or revoked by the city or state.
            </p>
            <v-spacer></v-spacer>
            <v-btn
              :href="statetaxNotesURL"
              color="info"
              target="_blank"
            >
              Notes
            </v-btn>
          </v-row>
        </v-container>
      </v-window-item>
      <v-window-item>
        <div class="text-h4">Authorized Contact & Signature</div>
        <ESignature
          v-model="general.statetax_sig_name"
          :accepted.sync="general.statetax_certified"
          :preamble="signaturePreamble"
        >
          <Contact
            :d="contactData"
            :roles="['State Tax Exemption Contact']"
            @assignContact="assignContact"
            contact-title
            role="State Tax Exemption Contact"
            description="The Contact for State Tax Exemption"
          />
        </ESignature>
      </v-window-item>
    </v-window>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import _ from 'lodash';
import AddressAutocomplete from "@/components/formfields/AddressAutocomplete.vue";
import ESignature from "@/components/formfields/ESignature.vue";
import Contact from '@/components/formfields/Contact.vue';

export default {
  name: 'Statetax',
  created() {
    const vm = this;
    vm.general = _.cloneDeep(vm.getQuoteInfo);
    this.populateAddedStates();
    if (!vm.general.statetax_name_of_firm) {
      vm.general.statetax_name_of_firm = vm.getQuoteFacts.company_name;
    }
    vm.hijackStepper(async (goRight) => {
      if ((vm.step === 0 && !goRight) || (vm.step === vm.maxStep && goRight)) {
        vm.unHijackStepper();
        vm.nextSubTab(goRight);
      }
      vm.step += (goRight ? 1 : -1);
    });
  },
  methods: {
    ...mapActions(['syncFormData', 'nextSubTab', 'hijackStepper', 'unHijackStepper']),
    sync() {
      const vm = this;
      vm.syncFormData({
        mainKey: 'general',
        data: _.cloneDeep(vm.general),
      });
    },
    setAddress(newAddress) {
      this.$set(this.general, 'statetax_address', newAddress.streetAddress);
      this.$set(this.general, 'statetax_suite', newAddress.suite);
      this.$set(this.general, 'statetax_city', newAddress.city);
      this.$set(this.general, 'statetax_state', newAddress.state);
      this.$set(this.general, 'statetax_zip', newAddress.zip);
    },
    queryForType(val) {
      if (val === 'other') {
        this.queryType = true;
      }
    },
    deleteState(i) {
      const deleted = this.addedStates.splice(i, 1);
      this.general[`statetax_${deleted[0].toLowerCase()}_id`] = null;
    },
    addState() {
      this.addedStates.push(null);
    },
    populateAddedStates() {
      const vm = this;
      _.forEach(vm.allStates, (state) => {
        if (vm.general[`statetax_${state.toLowerCase()}_id`] != null) {
          vm.addedStates.push(state);
        }
      });
    },
    assignContact(e) {
      this.$set(this.general, 'statetax_contact_name', e.fullname);
      this.$set(this.general, 'statetax_title', e.title);
    },
  },
  computed: {
    ...mapGetters(['getQuoteInfo', 'getQuoteFacts']),
    statetaxAddress() {
      const address = {};
      if (this.general.statetax_address != null) {
        address.streetAddress = this.general.statetax_address;
        address.suite = this.general.statetax_suite;
        address.city = this.general.statetax_city;
        address.state = this.general.statetax_state;
        address.zip = this.general.statetax_zip;
      }
      return address;
    },
    maxStep() {
      return 3;
    },
    filteredStates() {
      return _.differenceWith(this.allStates, this.addedStates, _.isEqual);
    },
    statetaxNotesURL() {
      return `/api/additional_information/${this.$store.state.contractId}/${this.$store.state.accessKey}/statetax_notes`;
    },
    contactData() {
      return {
        fullname: this.general.statetax_contact_name,
      };
    },
    contacts() {
      return this.getQuoteInfo.contacts;
    },
  },
  data() {
    return {
      general: {},
      step: 0,
      queryType: false,
      descriptionOfTaxableItems: `
        General description of tangible property or taxable services to be purchased from the seller
      `,
      companyTypes: [
        { text: 'Wholesaler', value: 'wholesaler' },
        { text: 'Retailer', value: 'retailer' },
        { text: 'Manufacturer', value: 'manufacturer' },
        { text: 'Seller (California)', value: 'seller' },
        { text: 'Lessor (see notes on pages 2-4)', value: 'lessor' },
        { text: 'Other (Specify)', value: 'other' },
      ],
      addedStates: [],
      allStates: [
        'AL', 'AR', 'AZ', 'CA', 'CO', 'CT', 'DC', 'FL', 'GA',
        'HI', 'ID', 'IL', 'IA', 'KS', 'KY', 'ME', 'MD', 'MI',
        'MN', 'MO', 'NE', 'NV', 'NJ', 'NM', 'NC', 'ND', 'OH',
        'OK', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
        'WA', 'WI',
      ],
      signaturePreamble: `
        Under penalties of perjury, I swear or affirm that the information on this form is true and correct as to every material matter.
      `,
      superscripts: {
        AL: '1',
        AZ: '2',
        CA: '3',
        CO: '4',
        CT: '5',
        DC: '6',
        FL: '7',
        GA: '8',
        HI: '4,9',
        IL: '4,10',
        KY: '11',
        ME: '12',
        MD: '13',
        MI: '14',
        MN: '15',
        MO: '16',
        NE: '17',
        NM: '4,18',
        NC: '19',
        OH: '20',
        OK: '21',
        PA: '22',
        RI: '23',
        SD: '24',
        TX: '25',
        WA: '26',
        WI: '27',
      },
    };
  },
  watch: {
    general: {
      handler() {
        this.sync();
      },
      deep: true,
    },
  },
  components: {
    AddressAutocomplete,
    ESignature,
    Contact,
  },
};
</script>
